import { defer, Observable } from 'rxjs';
import { $get } from 'services/api';
import { AuthTypes } from './auth.types';
import { getIdp } from './idp';

export type { AuthTypes } from './auth.types';

/**
 * The only boundary between this app and the Yabble IDP (AWS Cognito, CE-14388).
 * Everything above it — the user store, the login page, the router — is written against
 * these calls and knows nothing about what implements them: `services/cognito` in every
 * real build, `mocks/idp` under `VITE_ENABLE_MOCKS=true` (see `services/idp`).
 *
 * Every failure arrives as an `ApiTypes.Error` whose message is fit for the login page.
 */

/**
 * Resumes an existing session on a cold load, emitting `null` when there is none. The app
 * sits on the splash until this answers — which is the whole reason it exists as a
 * separate call, because guessing `unauthorized` first is what flashes the login page at
 * a user who is already signed in. An expired access token is refreshed on the way.
 */
export const $restoreSession = (): Observable<AuthTypes.Credentials | null> =>
  defer(() => getIdp().then((idp) => idp.restore()));

/**
 * Leaves the page for the IDP, carrying `returnTo` — the deep link the user set off from —
 * through the round trip. It does not come back here: the IDP redirects to the callback
 * route, which calls `$completeSignIn`. So this only ever *errors*, if the redirect cannot
 * start; until the page unloads the login button keeps its pending state.
 */
export const $signIn = (returnTo: string | null): Observable<void> =>
  defer(() => getIdp().then((idp) => idp.signIn(returnTo)));

/** The callback route's half of sign-in: exchanges the authorization code for tokens. */
export const $completeSignIn = (): Observable<AuthTypes.SignInResult> =>
  defer(() => getIdp().then((idp) => idp.completeSignIn()));

/** Drops the session from this browser only — for a `401`, where the IDP has nothing to end. */
export const $forgetSession = (): Observable<void> => defer(() => getIdp().then((idp) => idp.forget()));

/** Drops the session locally, then leaves the page to end it at the IDP too. */
export const $signOut = (): Observable<void> => defer(() => getIdp().then((idp) => idp.signOut()));

/**
 * Who the credentials belong to. Anyone Cognito lets sign in may use the tool, so this
 * decides nothing about access — `compliance-api` has no allowlist (CE-14394).
 *
 * `silent: true` because a failure here is shown on the login page, which the user store
 * sends them back to with the reason; a snackbar on top would say it twice.
 */
export const $loadProfile = (): Observable<AuthTypes.User> =>
  $get<AuthTypes.User>('me', {}, { silent: true });
